/**
 * Component: ConnectionStates
 *
 * Renders connection and loading states for the Wealthica v2 integration,
 * including auth loading, accounts loading, error states, and refresh notices.
 * Designed to avoid duplicate UI and to coordinate with the container.
 */

import React from 'react';
import { AlertCircle } from 'lucide-react';
import Spinner from '@/components/ui/Spinner';

interface ConnectionStatesProps {
  isAuthLoading: boolean;
  isWealthicaLoading: boolean;
  isWealthicaRefetching: boolean;
  wealthicaError: Error | null;
  isWealthicaConnected: boolean;
  hasConnections: boolean;
  tokenState: 'valid' | 'expired' | 'invalid' | 'missing';
  isLoadingPositions?: boolean;
  hasLoadedAccounts?: boolean;
  accountCount: number;
  onRetry: () => void;
  lastSyncAt?: Date | null;
}

export function ConnectionStates({
  isAuthLoading,
  isWealthicaLoading,
  isWealthicaRefetching,
  wealthicaError,
  isWealthicaConnected,
  accountCount,
  onRetry,
  lastSyncAt,
  tokenState = 'missing',
  hasConnections = false,
  isLoadingPositions = false,
  hasLoadedAccounts = false
}: ConnectionStatesProps) {
  // Consider data "loaded" once we've completed at least one accounts fetch cycle
  // This prevents a perpetual "Preparing your accounts..." state when there are
  // zero accounts but the fetch has finished (common with demo data or empty institutions).
  const hasFetchedOnce = !!lastSyncAt;
  // Enhanced error messaging logic
  const getErrorMessage = (error: Error | null, tokenState: string): { message: string; showReconnectLink: boolean } => {
    if (tokenState === 'expired') {
      return { message: "Session expired", showReconnectLink: true };
    }
    
    if (tokenState === 'malformed') {
      return { message: "Authentication error", showReconnectLink: true };
    }
    
    if (tokenState === 'missing') {
      return { message: "No connection", showReconnectLink: true };
    }
    
    if (error?.message?.includes('No bearer token')) {
      return { message: "Session expired", showReconnectLink: true };
    }
    
    // Existing error handling logic...
    return { message: error?.message || 'An unexpected error occurred', showReconnectLink: false };
  };
  // Handle auth loading state
  if (isAuthLoading) {
    return (
      <div className="py-4 flex justify-center items-center">
        <Spinner size="xs" variant="primary" className="mr-1.5" ariaLabel="Loading authentication" />
        <p className="text-muted-foreground text-xs">Loading authentication data...</p>
      </div>
    );
  }

  // Handle Wealthica loading state - improved timing logic
  if (isWealthicaLoading && !isWealthicaRefetching) {
    return (
      <div className="py-3 flex justify-center items-center">
        <Spinner size="xs" variant="primary" className="mr-1.5" ariaLabel="Loading accounts" />
        <p className="text-xs">Loading accounts...</p>
      </div>
    );
  }

  // Pending gap: connected + has connections but accounts not yet available
  // This restores the early first-load progress indicator without being noisy.
  if (
    isWealthicaConnected &&
    hasConnections &&
    !hasLoadedAccounts &&
    !hasFetchedOnce &&
    accountCount === 0 &&
    !isWealthicaRefetching
  ) {
    return (
      <div className="py-3 flex justify-center items-center">
        <Spinner size="xs" variant="primary" className="mr-1.5" ariaLabel="Preparing accounts" />
        <p className="text-xs">Preparing your accounts...</p>
      </div>
    );
  }

  // Handle position loading state - only show if accounts are loaded but positions still loading
  if (hasLoadedAccounts && isLoadingPositions && isWealthicaConnected) {
    return (
      <div className="py-3 flex justify-center items-center">
        <Spinner size="xs" variant="primary" className="mr-1.5" ariaLabel="Loading positions" />
        <p className="text-xs">Loading positions...</p>
      </div>
    );
  }

  // Handle error state - but skip if no database connections exist (prevents false "Session Expired")
  if (wealthicaError && hasConnections) {
    const errorInfo = getErrorMessage(wealthicaError, tokenState);
    
    return (
      <div className="text-center py-8">
        <AlertCircle className="mx-auto h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {tokenState === 'valid' ? 'Connection Error' : 'Session Expired'}
        </h3>
        <p className="text-gray-600 mb-4">
          {errorInfo.message}
          {errorInfo.showReconnectLink && (
            <>
              :{' '}
              <button
                onClick={onRetry}
                className="text-blue-600 hover:underline font-medium"
              >
                refresh
              </button>
            </>
          )}
        </p>
        {!errorInfo.showReconnectLink && (
          <button
            onClick={onRetry}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // Not connected - only show if we actually expect to be connected
  if (!isWealthicaConnected && hasConnections) {
    return (
      <div className="py-2 text-center">
        <h3 className="text-sm font-semibold mb-1">Not Connected to Wealthica</h3>
        <p className="text-xs text-muted-foreground">
          Connect your Wealthica account to view your investment data.
        </p>
        {/* Connect button removed to avoid duplication with the main page connect button */}
      </div>
    );
  }

  // Connected but no accounts - REMOVED: Useless state that redirects to external site
  // Skip this state entirely and let the container handle empty account states gracefully
  // if (isWealthicaConnected && accountCount === 0) { ... } // REMOVED

  // Connected with accounts - don't show anything to avoid duplicate headers
  // Only show minimal refreshing indicator if needed
  return isWealthicaRefetching ? (
    <div className="flex items-center justify-end text-xs text-muted-foreground mb-1">
      <Spinner size="xs" variant="primary" className="mr-1" ariaLabel="Refreshing" />
      <span>Refreshing...</span>
    </div>
  ) : null; // Return null to avoid rendering anything when accounts are connected
}

// Export as default for flexibility
export default ConnectionStates;
